// @owners { team: patients }
import { BORDERS, COLORS, SPACING } from '@alto/design-library-tokens';
import { AccessibilityInfo, MdPadding, Row } from '@alto/design-system';
import React, { useEffect, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import styledNative from 'styled-components/native';

const DEFAULT_ANIMATION_DURATION = 2000; // 2 seconds

const FORWARDS = 'forwards';
const BACKWARDS = 'backwards';

export type ProgressProps = {
  readonly completedSteps: number;
  readonly totalSteps: number;
};

const Step = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: row;
  height: ${SPACING.STATIC.XXS.px};
  border-radius: ${BORDERS.RADIUS.XS.px};
  margin: 0 ${SPACING.STATIC.XXS.px};
`;

const IncompleteStep = styled(Step)<{ backgroundColor: string }>`
  background-color: ${({ backgroundColor }) => backgroundColor};
`;

const growWidth = keyframes`
  from { width: 0%; }
  to { width: 100%; }
`;

const shrinkWidth = keyframes`
  from { width: 100%; }
  to { width: 0%; }
`;

type AnimationDirectionType = typeof FORWARDS | typeof BACKWARDS;

const GrowingWidthWrapper = styled.div<{ animationDirection: AnimationDirectionType; isReduceMotionEnabled: boolean }>`
  ${({ isReduceMotionEnabled, animationDirection }) =>
    !isReduceMotionEnabled &&
    css`
      animation-duration: ${DEFAULT_ANIMATION_DURATION}ms;
      animation-name: ${animationDirection === FORWARDS ? growWidth : shrinkWidth};
      animation-fill-mode: forwards;
    `}
`;

const CompletedStep = styled(Step)<{ noMargin?: boolean; backgroundColor: string }>`
  background-color: ${({ backgroundColor }) => backgroundColor};
  ${({ noMargin }) => (noMargin ? 'margin: 0;' : '')};
`;

const ProgressContainer = styledNative(Row)`
  width: 240px;
`;

const OnboardingProgress = ({ completedSteps, totalSteps }: ProgressProps) => {
  const [animationDirection, setAnimationDirection] = useState<AnimationDirectionType>(FORWARDS);
  const [isReduceMotionEnabled, setIsReduceMotionEnabled] = useState(true);
  const [prevCompletedSteps, setPrevCompletedSteps] = useState(completedSteps);

  // Adjust the animation direction during render when completedSteps changes (no Effect needed).
  if (prevCompletedSteps !== completedSteps) {
    setAnimationDirection(completedSteps > prevCompletedSteps ? FORWARDS : BACKWARDS);
    setPrevCompletedSteps(completedSteps);
  }

  useEffect(() => {
    // Tracks whether this effect instance is still current: `completedSteps` can change (or the
    // component can unmount) before `isReduceMotionEnabled` resolves, and we should not update state then.
    let isActive = true;
    void (async () => {
      try {
        const enabled = await AccessibilityInfo.isReduceMotionEnabled();
        if (isActive) {
          setIsReduceMotionEnabled(enabled);
        }
      } catch {
        // Keep existing isReduceMotionEnabled if the API fails
      }
    })();
    // When dependencies change or on unmount, mark this run stale so the in-flight async work cannot set state.
    return () => {
      isActive = false;
    };
  }, [completedSteps]);

  const steps = [];

  for (let step = 0; step < totalSteps; step++) {
    if (
      // If this was the last completed step, animate it from incomplete to complete
      (animationDirection === FORWARDS && step === completedSteps - 1) || // If we're going backwards to this step, animate the one after it from complete to incomplete
      (animationDirection === BACKWARDS && step === completedSteps)
    ) {
      steps.push(
        <IncompleteStep backgroundColor={COLORS.BACKGROUND_COLORS.PROGRESS_BAR_INCOMPLETE}>
          <GrowingWidthWrapper
            isReduceMotionEnabled={isReduceMotionEnabled}
            animationDirection={animationDirection}
          >
            <CompletedStep
              backgroundColor={COLORS.BACKGROUND_COLORS.PROGRESS_BAR_COMPLETE}
              noMargin
            />
          </GrowingWidthWrapper>
        </IncompleteStep>,
      );
    } else if ((animationDirection === BACKWARDS && step === completedSteps - 1) || step < completedSteps - 1) {
      steps.push(<CompletedStep backgroundColor={COLORS.BACKGROUND_COLORS.PROGRESS_BAR_COMPLETE} />);
    } else {
      steps.push(<IncompleteStep backgroundColor={COLORS.BACKGROUND_COLORS.PROGRESS_BAR_INCOMPLETE} />);
    }
  }

  return (
    <MdPadding>
      <ProgressContainer>{steps}</ProgressContainer>
    </MdPadding>
  );
};

export default OnboardingProgress;
